import React from 'react';
import { Button, Flex, Text, TextField, Select } from '@chatsdet/ui';
import { Plus, Trash } from '@phosphor-icons/react';
import type { ActionDataRequirement, ActionInputType, ActionInputValues } from '@chatsdet/types';
import { MacroRichTextField } from './MacroRichTextField';
import { labelToSnakeName, uniqueName } from './nameUtils';

const ADD_FIELD_TYPE_OPTIONS: { id: ActionInputType; label: string }[] = [
  { id: 'text', label: 'Text' },
  { id: 'email', label: 'Email' },
  { id: 'password', label: 'Password' },
  { id: 'number', label: 'Number' },
  { id: 'textarea', label: 'Textarea' },
];

type ActionInputsEditorProps = {
  dataRequirements: ActionDataRequirement[];
  inputValues: ActionInputValues | undefined;
  lastRequirementUpdateAt?: string;
  onChange: (
    updater: (current: ActionInputValues | undefined) => ActionInputValues | undefined
  ) => void;
  onAddRequirement?: (requirement: ActionDataRequirement) => void;
  onDeleteRequirement?: (name: string) => void;
  onClearAll?: () => void;
};

export const ActionInputsEditor: React.FC<ActionInputsEditorProps> = ({
  dataRequirements,
  inputValues,
  lastRequirementUpdateAt,
  onChange,
  onAddRequirement,
  onDeleteRequirement,
  onClearAll,
}) => {
  const [isAdding, setIsAdding] = React.useState(false);
  const [newLabel, setNewLabel] = React.useState('');
  const [newType, setNewType] = React.useState<ActionInputType>('text');

  const handleValueChange = React.useCallback(
    (field: string, nextValue: string) => {
      onChange((current) => {
        const next = { ...current };
        if (nextValue.trim().length === 0) {
          delete next[field];
        } else {
          next[field] = nextValue;
        }
        return Object.keys(next).length > 0 ? next : undefined;
      });
    },
    [onChange]
  );

  const requirementsUpdatedLabel = React.useMemo(() => {
    if (!lastRequirementUpdateAt) {
      return null;
    }
    try {
      return new Date(lastRequirementUpdateAt).toLocaleString();
    } catch {
      return lastRequirementUpdateAt;
    }
  }, [lastRequirementUpdateAt]);

  const resetAddForm = React.useCallback(() => {
    setIsAdding(false);
    setNewLabel('');
    setNewType('text');
  }, []);

  const handleSubmitNewField = React.useCallback(() => {
    const trimmed = newLabel.trim();
    if (!trimmed || !onAddRequirement) return;
    const baseName = labelToSnakeName(trimmed);
    if (!baseName) return;
    const existingNames = dataRequirements.map((r) => r.name);
    const name = uniqueName(baseName, existingNames);
    onAddRequirement({ name, label: trimmed, type: newType, required: true });
    resetAddForm();
  }, [newLabel, newType, dataRequirements, onAddRequirement, resetAddForm]);

  const addFieldButton = onAddRequirement ? (
    isAdding ? (
      <div className="mt-3 rounded-md border border-border/60 p-3 space-y-3">
        <TextField
          label="Field label"
          placeholder="e.g. First Name"
          value={newLabel}
          onChange={setNewLabel}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSubmitNewField();
            } else if (e.key === 'Escape') {
              resetAddForm();
            }
          }}
        />
        <Select
          label="Type"
          items={ADD_FIELD_TYPE_OPTIONS}
          selectedKey={newType}
          onSelectionChange={(key) => {
            if (key) setNewType(key as ActionInputType);
          }}
        />
        <Flex gap="sm" justify="end">
          <Button variant="ghost" size="sm" onPress={resetAddForm}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            isDisabled={!newLabel.trim() || !labelToSnakeName(newLabel.trim())}
            onPress={handleSubmitNewField}
          >
            Add
          </Button>
        </Flex>
      </div>
    ) : (
      <div className="mt-3">
        <Button
          variant="outline"
          size="sm"
          iconLeft={<Plus size={14} />}
          onPress={() => setIsAdding(true)}
        >
          Add field
        </Button>
      </div>
    )
  ) : null;

  if (dataRequirements.length === 0) {
    return (
      <div>
        <Text size="sm" className="text-muted-foreground">
          Provide the values the agent should use when it performs this action. These fields are
          captured automatically if a run fails and the agent reports missing inputs.
        </Text>
        <Text size="sm" className="text-muted-foreground mt-3">
          No data requirements have been captured yet. Run the workflow to let the agent describe
          the inputs it needs, or add fields manually below.
        </Text>
        {addFieldButton}
      </div>
    );
  }

  return (
    <div>
      {onClearAll && (
        <Flex align="center" justify="end" className="mb-2">
          <Button variant="ghost" size="sm" onPress={onClearAll} className="text-muted-foreground">
            Clear all
          </Button>
        </Flex>
      )}
      <Text size="sm" className="text-muted-foreground">
        Provide the values the agent should use when it performs this action. These fields are
        captured automatically if a run fails and the agent reports missing inputs.
      </Text>
      {requirementsUpdatedLabel && (
        <Text size="xs" className="text-muted-foreground mt-1">
          Captured {requirementsUpdatedLabel}
        </Text>
      )}

      <div className="mt-3 space-y-3">
        {dataRequirements.map((req) => {
          const baseLabel = `${req.label}${req.required === false ? ' (optional)' : ''}`;
          const descriptionParts = [
            req.description,
            req.exampleValue ? `Example: ${req.exampleValue}` : null,
          ].filter(Boolean);
          const helperText = descriptionParts.length > 0 ? descriptionParts.join(' ') : undefined;

          if (req.type === 'select' && req.options && req.options.length > 0) {
            const options = req.options.map((opt) => ({
              id: opt.value,
              label: opt.label,
            }));
            const selectedKey = inputValues?.[req.name] ?? null;
            const isMissing =
              (selectedKey === null || String(selectedKey).trim().length === 0) &&
              req.required !== false;

            return (
              <div key={req.name} className="flex flex-col gap-1">
                <Flex align="start" gap="sm">
                  <div className="flex-1">
                    <Select
                      label={baseLabel}
                      placeholder={req.placeholder || 'Select an option'}
                      items={options}
                      selectedKey={selectedKey ?? null}
                      onSelectionChange={(key) =>
                        handleValueChange(req.name, key ? String(key) : '')
                      }
                    />
                  </div>
                  {onDeleteRequirement && (
                    <div className="pt-6">
                      <Button
                        variant="icon"
                        size="sm"
                        iconOnly
                        tooltip="Remove field"
                        onPress={() => onDeleteRequirement(req.name)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash size={14} />
                      </Button>
                    </div>
                  )}
                </Flex>
                {isMissing && (
                  <Text size="xs" className="text-destructive">
                    Required
                  </Text>
                )}
                {helperText && (
                  <Text size="xs" className="text-muted-foreground">
                    {helperText}
                  </Text>
                )}
              </div>
            );
          }

          const inputType = req.type === 'email' || req.type === 'password' ? req.type : 'text';
          const currentValue = inputValues?.[req.name] ?? '';
          const isMissing = currentValue.trim().length === 0 && req.required !== false;

          // Use MacroRichTextField for text/email/number types (not password — macros in passwords don't make sense)
          const useMacroField = req.type !== 'password';

          return (
            <div key={req.name} className="flex flex-col gap-1">
              <Flex align="start" gap="sm">
                <div className="flex-1">
                  {useMacroField ? (
                    <MacroRichTextField
                      label={baseLabel}
                      placeholder={req.placeholder ?? 'Type $ for macros'}
                      value={currentValue}
                      onChange={(value) => handleValueChange(req.name, value)}
                      description={helperText}
                      type={inputType}
                      errorMessage={isMissing ? 'Required' : undefined}
                    />
                  ) : (
                    <TextField
                      label={baseLabel}
                      placeholder={req.placeholder}
                      value={currentValue}
                      onChange={(value) => handleValueChange(req.name, value)}
                      description={helperText}
                      type={inputType}
                      errorMessage={isMissing ? 'Required' : undefined}
                    />
                  )}
                </div>
                {onDeleteRequirement && (
                  <div className="pt-6">
                    <Button
                      variant="icon"
                      size="sm"
                      iconOnly
                      tooltip="Remove field"
                      onPress={() => onDeleteRequirement(req.name)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                )}
              </Flex>
            </div>
          );
        })}
      </div>

      {addFieldButton}
    </div>
  );
};
